import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { LOGIN } from "@/constants/testIds";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      const from = loc.state?.from?.pathname || "/app/dashboard";
      nav(from);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-stretch bg-[#0B0D12] text-[#F4F1EB]">
      <div className="hidden lg:flex flex-1 relative grain">
        <img
          src="https://images.pexels.com/photos/8319695/pexels-photo-8319695.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B0D12] via-[#0B0D12]/60 to-transparent"></div>
        <div className="relative z-10 p-16 flex flex-col justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/orbital-logo.png" alt="Orbital" className="w-9 h-9 object-contain" />
            <div className="font-display text-xl font-bold">Orbital</div>
          </Link>
          <div>
            <h2 className="font-display text-5xl font-black leading-none max-w-md">
              Sign in to your <span className="text-[#7C5CFF]">trading floor</span>.
            </h2>
            <p className="mt-4 text-[#A3A9B8] max-w-sm">
              EUR, USD, CAD and ten cryptos — all in one calm, well-built dashboard.
            </p>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 lg:p-16">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8">
            <Link to="/" className="flex items-center gap-2">
              <img src="/orbital-logo.png" alt="Orbital" className="w-9 h-9 object-contain" />
              <div className="font-display text-xl font-bold">Orbital</div>
            </Link>
          </div>
          <div className="text-xs tracking-[0.3em] uppercase text-[#7C5CFF] mb-3">Welcome back</div>
          <h1 className="font-display text-3xl font-bold mb-8">Sign in to Orbital</h1>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase text-[#A3A9B8]">Email</label>
              <input
                data-testid={LOGIN.emailInput}
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 w-full bg-[#0B0D12] border border-[#2A2E38] rounded-sm px-4 py-3 focus:border-[#7C5CFF] focus:outline-none transition-colors"
                placeholder="you@email.com"
                autoComplete="email"
              />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase text-[#A3A9B8]">Password</label>
              <input
                data-testid={LOGIN.passwordInput}
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 w-full bg-[#0B0D12] border border-[#2A2E38] rounded-sm px-4 py-3 focus:border-[#7C5CFF] focus:outline-none transition-colors"
                placeholder="••••••••"
                autoComplete="current-password"
              />
            </div>
            <button
              data-testid={LOGIN.submitButton}
              type="submit"
              disabled={busy}
              className="w-full py-3 bg-[#7C5CFF] text-white rounded-sm hover:bg-[#9C82FF] disabled:opacity-60 transition-colors"
            >
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <p className="mt-6 text-sm text-[#A3A9B8]">
            Don&apos;t have an account?{" "}
            <Link
              to="/register"
              data-testid={LOGIN.registerLink}
              className="text-[#7C5CFF] hover:text-[#9C82FF]"
            >
              Create one
            </Link>
          </p>

          <div className="mt-10 p-4 border border-[#2A2E38] rounded-sm text-xs text-[#6E7587]">
            Trouble signing in? Contact support at support@orbital.exchange.
          </div>
        </div>
      </div>
    </div>
  );
}
